import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { FileSignature, Clock } from 'lucide-react';
import { useTranslation } from '../i18n';

function formatSignatureDate(ts) {
  if (ts == null) return '';
  const str = typeof ts === 'string' ? (ts.endsWith('Z') ? ts : `${ts}Z`) : ts;
  const date = new Date(str);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString();
}

/**
 * Renders a visual signature image properly.
 * Gracefully degrades to native base64 strings or utilizes AWS S3 temporary URLs.
 */
function SignatureImage({ signature }) {
  const [url, setUrl] = useState(signature.signature_data || null);
  const [loading, setLoading] = useState(!signature.signature_data && !!signature.storage_path);

  useEffect(() => {
    let mounted = true;
    if (signature.storage_path && !signature.signature_data) {
      api.getSignaturePresignedUrl(signature._id)
        .then((signedUrl) => {
          if (mounted) {
            setUrl(signedUrl);
            setLoading(false);
          }
        })
        .catch((err) => {
          console.error('Failed to load signature image', err);
          if (mounted) setLoading(false);
        });
    }
    return () => {
      mounted = false;
    };
  }, [signature]);

  if (loading) {
    return <div className="h-16 w-32 bg-muted/50 animate-pulse rounded-md" />;
  }

  if (!url) return null;

  return (
    <img
      src={url}
      alt="Signature"
      className="max-h-20 w-auto object-contain"
    />
  );
}

/**
 * SignaturesListModal
 * Shows deal owner and clients with Signed (name, date, signature image) or Pending.
 * Used in Deal Room when contract is pending signature.
 */
export function SignaturesListModal({
  open,
  onOpenChange,
  deal,
  clients = [],
  signatures,
}) {
  const { t } = useTranslation();
  const signatureList = signatures?.signatures ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[95vw] max-w-lg max-h-[90vh] flex flex-col p-0 gap-0"
        data-testid="signatures-list-dialog"
      >
        <DialogHeader className="p-6 pb-2 flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <FileSignature className="h-5 w-5 text-primary" />
            {t('dealRoom.signaturesList.title') || 'Signatures'}
          </DialogTitle>
          <DialogDescription>
            {t('dealRoom.pendingSignature.message')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto overflow-x-hidden px-6 py-4 min-h-0 space-y-6">
          {/* Deal owner section */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              {t('dealRoom.signaturesList.dealOwner') || 'Deal owner'}
            </h4>
            <div className="rounded-lg border bg-card p-4">
              {(() => {
                const ownerSignature = signatureList.find(
                  (s) => String(s.signer_user_id) === String(deal?.owner_user_id)
                );
                if (ownerSignature) {
                  return (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-medium">
                          {ownerSignature.signer_name ||
                            t('dealRoom.signaturesList.dealOwner')}
                        </p>
                        <span className="text-xs text-muted-foreground">
                          {t('dealRoom.signaturesList.signedOn')}{' '}
                          {formatSignatureDate(ownerSignature.signed_at)}
                        </span>
                      </div>
                      {(ownerSignature.signature_data || ownerSignature.storage_path) && (
                        <div className="border rounded-md bg-muted/20 p-2 flex items-center justify-center min-h-[80px]">
                          <SignatureImage signature={ownerSignature} />
                        </div>
                      )}
                    </div>
                  );
                }
                return (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Clock className="h-4 w-4 flex-shrink-0" />
                    <span>
                      {t('dealRoom.signaturesList.dealOwner') || 'Deal owner'}
                    </span>
                    <span className="text-xs">
                      — {t('dealRoom.signaturesList.pending') || 'Pending'}
                    </span>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Clients section */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              {t('dealRoom.signaturesList.clients') || 'Clients'}
            </h4>
            {clients && clients.length > 0 ? (
              <div className="space-y-3">
                {clients.map((client) => {
                  const clientId = String(client._id);
                  const linkedUserId = client.linked_user_id
                    ? String(client.linked_user_id)
                    : null;
                  const clientSignature = signatureList.find(
                    (s) =>
                      String(s.signer_client_id) === clientId ||
                      (linkedUserId &&
                        String(s.signer_user_id) === linkedUserId)
                  );
                  return (
                    <div
                      key={client._id}
                      className="rounded-lg border bg-card p-4"
                    >
                      {clientSignature ? (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between gap-2">
                            <p className="font-medium">
                              {clientSignature.signer_name ||
                                client.name ||
                                'Client'}
                            </p>
                            <span className="text-xs text-muted-foreground">
                              {t('dealRoom.signaturesList.signedOn')}{' '}
                              {formatSignatureDate(clientSignature.signed_at)}
                            </span>
                          </div>
                          {(clientSignature.signature_data || clientSignature.storage_path) && (
                            <div className="border rounded-md bg-muted/20 p-2 flex items-center justify-center min-h-[80px]">
                              <SignatureImage signature={clientSignature} />
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Clock className="h-4 w-4" />
                          <span>{client.name || 'Client'}</span>
                          <span className="text-xs">
                            — {t('dealRoom.signaturesList.pending') || 'Pending'}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground">
                {t('dealRoom.details.clients') || 'Clients'}:{' '}
                {t('common.none')}
              </div>
            )}
          </div>
        </div>

        <div className="p-6 pt-2 flex-shrink-0 border-t bg-background">
          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.close')}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default SignaturesListModal;
